import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal as SmallModal } from '@utils/RNModal'
import { Animated, Platform, Dimensions, ActivityIndicator } from "react-native";
import { useDispatch } from 'react-redux';
import { View, Text } from '@constants/Themed';
import styles from "@stylesheet";
import LottieView from '@utils/lottieAnimation';
import { CLEAR_COMPANY, HIDE_ALL_MODALS, HIDE_LOADING_MODAL, LOGGED_OUT, SUCCESS, SWITCH_COMPANY_REQUEST, UPDATE_USER } from "@reduxLocal/actions/types";
import { store } from "@reduxLocal/persistState";
import apis from "@api/api";
import { CommonActions, useNavigation, useFocusEffect, useIsFocused } from "@react-navigation/native";
import { consumePendingDashboardRoute } from '@navigation/LinkingConfiguration';
import { clearPendingDashboardDeepLink } from '@navigation/utils/pendingDashboardDeepLink';
import { resetToDashboardStackRoot } from '@navigation/utils/resetToDashboardStackRoot';
import { dashboardAreaFromRouteNames } from '@navigation/utils/sessionContextNavigation';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { clearSessionCookie } from '@utils/sessionCookie';
import { persistAuthSession } from '@utils/persistentSessionStorage';
import { buildSessionScopeId } from '@api/utils/sessionScope';
import { recordOnlineContextAccess } from '@api/utils/recentOnlineContextAccess';
import { applyTargetUserSessionContext } from '@api/utils/applyTargetUserSessionContext';
import {
  isDefiniteSessionProbeFailure,
  isTransientRequestError,
} from '@utils/sessionExpiryDetection';
import { hasPersistedWebSession } from '@utils/hasPersistedWebSession';
import {
  consumePendingEasyModeDeepLink,
  extractEasyModePromptFromPath,
  isEasyModeHelpDeepLinkPath,
  peekPendingMarketingPrompt,
  stashPendingEasyModeDeepLink,
} from '@navigation/screens/MidLevel/EasyModeScreen/utils/easyModeDeepLink';
import { consumePendingPreviewId } from '@navigation/screens/TopLevel/PreviewScreen/utils/pendingPreview';

// Persist login URL params (code, productId) on first load - they can be lost when linking syncs to Loading
const PRESERVED_LOGIN_PARAMS_KEY = 'smartsaas_login_params';
const getPreservedLoginParams = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PRESERVED_LOGIN_PARAMS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const setPreservedLoginParams = (params: { code?: string; productId?: string } | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (params && (params.code || params.productId)) {
      sessionStorage.setItem(PRESERVED_LOGIN_PARAMS_KEY, JSON.stringify(params));
    } else {
      sessionStorage.removeItem(PRESERVED_LOGIN_PARAMS_KEY);
    }
  } catch {
    /* ignore */
  }
};

const WEB_LOADING_BG = 'var(--ss-nav-background, #eef4fb)';

const LoadingScreen = ({ route }) => {
  const [progress, setProgress] = useState(0);
  const [text, setText] = useState("");
  const nativeDimsRef = useRef(Dimensions.get('window'));
  const sessionFetchStartedRef = useRef(false);
  const sessionResolvedRef = useRef(false);
  const screenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useDispatch();
  const navigation = useNavigation<any>();
  const isFocused = useIsFocused();
  const holdFlag = Boolean(route?.params?.flagHold ?? route?.params?.params?.flagHold);
  const theme = themeSelector();
  // Capture login URL params (code, productId) on mount - they may be lost when linking syncs URL to Loading
  const capturedLoginParamsRef = React.useRef<{ code?: string; productId?: string } | null | undefined>(undefined);
  if (Platform.OS === 'web' && typeof window !== 'undefined' && capturedLoginParamsRef.current === undefined) {
    const path = window.location.pathname;
    const search = window.location.search;
    let params: { code?: string; productId?: string } | null = null;
    if (path.includes('login') || path.includes('auth')) {
      const urlParams = new URLSearchParams(search);
      const code = urlParams.get('code') || urlParams.get('promoCode');
      const productId = urlParams.get('productId') || undefined;
      if (code || productId) {
        params = { code: code || undefined, productId };
        setPreservedLoginParams(params);
      }
    }
    // Belt-and-suspenders: preserve Easy Mode /help deep link across Loading redirect
    const fullPath = `${path}${search || ''}`;
    if (isEasyModeHelpDeepLinkPath(fullPath)) {
      stashPendingEasyModeDeepLink(extractEasyModePromptFromPath(fullPath));
    }
    capturedLoginParamsRef.current = params ?? {};
  }
  const loadingMessage =
    route?.params?.loadingMessage ??
    route?.params?.params?.loadingMessage ??
    'Loading...';

  // Helper function to safely navigate
  const safeNavigate = (screenName: string, params?: any) => {
    if (isFocused && navigation) {
      try {
        // Check if we're on /learn route and preserve learning mode
        let navigationParams = params || {};
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const isLearnRoute = window.location.pathname.includes('/learn');
          const urlParams = new URLSearchParams(window.location.search);
          const hasLearningParam = urlParams.get('learning') === 'true' || urlParams.get('learn') === 'true';
          
          if (isLearnRoute || hasLearningParam) {
            navigationParams = {
              ...navigationParams,
              learningMode: true,
            };
            // Use Learn screen instead of Login if on /learn route
            if (screenName === 'Login' && isLearnRoute) {
              screenName = 'Learn';
            }
          }
        }
        
        // Map screen names for web
        // On web, both Login and Learn go to Auth stack
        if (Platform.OS === 'web') {
          if (screenName === 'Login' || screenName === 'Learn') {
            // Navigate to the specific screen within Auth stack
            // Use replace to prevent back navigation issues
            try {
              const stored = capturedLoginParamsRef.current || getPreservedLoginParams();
              const loginParams = stored && (stored.code || stored.productId) ? stored : null;
              const hasCode = loginParams?.code || navigationParams?.code;
              const hasProductId = loginParams?.productId || navigationParams?.productId;

              const finalParams =
                screenName === 'Learn' && navigationParams.learningMode
                  ? navigationParams
                  : screenName === 'Login' && (hasCode || hasProductId)
                    ? { code: hasCode || undefined, productId: hasProductId || undefined }
                    : {};

              // Signed-out visitor from the marketing "Build Your Business" form: preview first.
              // The prompt stays stashed so sign-up from the preview still applies it.
              if (screenName === 'Login' && !hasCode && !hasProductId && peekPendingMarketingPrompt()) {
                navigation.navigate('Auth' as never, { screen: 'Preview' } as never);
                if (typeof window !== 'undefined') {
                  const previewUrl = new URL(window.location.href);
                  previewUrl.pathname = '/preview';
                  previewUrl.search = '';
                  window.history.replaceState({}, '', previewUrl.toString());
                }
                return;
              }

              navigation.navigate('Auth' as never, {
                screen: screenName,
                params: finalParams,
              } as any);

              // Update URL to match - preserve code/productId for Login
              if (typeof window !== 'undefined') {
                const newUrl = new URL(window.location.href);
                if (screenName === 'Learn') {
                  newUrl.pathname = '/learn';
                  newUrl.searchParams.delete('learningMode');
                  newUrl.searchParams.delete('learning');
                  newUrl.searchParams.delete('learn');
                } else if (screenName === 'Login') {
                  newUrl.pathname = '/login';
                  newUrl.searchParams.delete('learningMode');
                  newUrl.searchParams.delete('learning');
                  newUrl.searchParams.delete('learn');
                  if (finalParams?.code) newUrl.searchParams.set('code', finalParams.code);
                  if (finalParams?.productId) newUrl.searchParams.set('productId', finalParams.productId);
                }
                window.history.replaceState({}, '', newUrl.toString());
              }
              return;
            } catch (navError) {
              console.error('Navigation error in Auth stack:', navError);
              // Fallback: try direct navigation without params
              navigation.navigate('Auth' as never, screenName === 'Learn' ? { learningMode: true } : {});
              return;
            }
          }
        }
        // Prefer replace so LoadingScreen is not left under Dashboard in LoginStack
        // (otherwise SearchBar / DefaultScreen back can pop back into the loading screen).
        if (screenName === 'Dashboard') {
          const returnArea =
            route?.params?.returnArea ?? route?.params?.params?.returnArea;
          if (typeof returnArea === 'string' && returnArea.trim()) {
            // Company switch: drop the previous company's document URL and reopen
            // the current area at its root screen.
            const area =
              dashboardAreaFromRouteNames(['Dashboard', returnArea.trim()]) ??
              returnArea.trim();
            clearPendingDashboardDeepLink();
            resetToDashboardStackRoot(area);
            return;
          }
          const pendingEasyMode = consumePendingEasyModeDeepLink();
          const pendingDashboard = consumePendingDashboardRoute();
          if (Platform.OS === 'web' && !pendingEasyMode && pendingDashboard) {
            navigation.dispatch(
              CommonActions.reset({
                index: 0,
                routes: [
                  pendingDashboard.kind === 'route'
                    ? pendingDashboard.route
                    : { name: 'NotFound' },
                ],
              })
            );
            return;
          }
          const previewId =
            pendingEasyMode?.source === 'marketing' ? consumePendingPreviewId() : undefined;
          const dashboardParams = pendingEasyMode
            ? {
                screen: 'EasyMode',
                params: pendingEasyMode.initialUserPrompt
                  ? {
                      initialUserPrompt: pendingEasyMode.initialUserPrompt,
                      initialPromptNonce:
                        pendingEasyMode.initialPromptNonce ?? Date.now(),
                      ...(previewId ? { previewId } : {}),
                    }
                  : undefined,
              }
            : navigationParams;

          if (typeof (navigation as any).replace === 'function') {
            (navigation as any).replace(screenName as never, dashboardParams);
            return;
          }
          navigation.navigate(screenName as never, dashboardParams);
          return;
        }
        navigation.navigate(screenName as never, navigationParams);
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback for web navigation structure
        if (Platform.OS === 'web') {
          if (screenName === 'Login' || screenName === 'Learn') {
            const isLearnRoute = typeof window !== 'undefined' && window.location.pathname.includes('/learn');
            navigation.navigate('Auth' as never, isLearnRoute ? { learningMode: true } : {});
          } else if (screenName === 'Dashboard') {
            navigation.navigate('Dashboard' as never);
          }
        }
      }
    }
  };

  const markSessionResolved = useCallback(() => {
    sessionResolvedRef.current = true;
    if (screenTimeoutRef.current) {
      clearTimeout(screenTimeoutRef.current);
      screenTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Fallback if session probe hangs — do not fire after successful navigation.
    screenTimeoutRef.current = setTimeout(() => {
      if (sessionResolvedRef.current) return;
      console.warn('Loading screen timeout - navigating to login');
      const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
      markSessionResolved();
      if (isLearnRoute) {
        safeNavigate('Learn', { learningMode: true });
      } else {
        safeNavigate('Login', {});
      }
    }, 10000);

    // Only wait for a *user-initiated* company switch while holdFlag is set.
    // fetchSession also dispatches CLEAR_COMPANY / SWITCH_COMPANY_REQUEST during bootstrap;
    // subscribing on every mount caused a navigate→remount loop (blue fast-refresh toast).
    if (!holdFlag) {
      return () => {
        if (screenTimeoutRef.current) {
          clearTimeout(screenTimeoutRef.current);
          screenTimeoutRef.current = null;
        }
      };
    }

    const unsubscribe = store.subscribe(() => {
      if (sessionResolvedRef.current) return;
      const lastType = store.getState().lastAction?.type;
      if (
        lastType === 'USER COMPANY CHANGED' ||
        lastType === 'USER COMPANY CHANGE REQUESTED' ||
        lastType === 'CLEARED COMPANY'
      ) {
        console.log('User company changed, navigating to Dashboard');
        markSessionResolved();
        safeNavigate('Dashboard');
        dispatch({ type: HIDE_ALL_MODALS });
        unsubscribe();
      }
    });

    return () => {
      if (screenTimeoutRef.current) {
        clearTimeout(screenTimeoutRef.current);
        screenTimeoutRef.current = null;
      }
      unsubscribe();
    };
  }, [dispatch, holdFlag, isFocused, markSessionResolved]);

  const updateProgress = useCallback((nextProgress: number, stateText: string) => {
    return new Promise<void>((resolve) => {
      if (Platform.OS === 'web') {
        setProgress(nextProgress);
        setText((prev) => (prev === stateText ? prev : stateText));
        resolve();
        return;
      }
      setProgress(nextProgress);
      setText(stateText);
      if (progressTimeoutRef.current) clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = setTimeout(() => {
        progressTimeoutRef.current = null;
        resolve();
      }, 500);
    });
  }, []);

  const clearStaleAuthIfNeeded = useCallback(async () => {
    const state = store.getState();
    const hasStaleAuth =
      Boolean(state.promiseData?.isAuthenticated) ||
      Boolean(state.promiseData?.data?.userData?.id);

    if (!hasStaleAuth) return;

    dispatch({ type: LOGGED_OUT });
    try {
      const { persistor } = require('@reduxLocal/persistState');
      await persistor?.purge?.();
    } catch (e) {
      if (__DEV__) console.warn('[LoadingScreen] persistor.purge failed:', e);
    }
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current) clearTimeout(progressTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    async function fetchSession() {
      if (sessionFetchStartedRef.current) return;
      sessionFetchStartedRef.current = true;
      try {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const checkoutSessionId = urlParams.get('session_id');
          if (checkoutSessionId) {
            try {
              await updateProgress(0.05, 'Confirming subscription...');
              await apis.syncCheckoutSession({ sessionId: checkoutSessionId });
              await apis.refreshUser();
              const cleanUrl = new URL(window.location.href);
              cleanUrl.searchParams.delete('session_id');
              window.history.replaceState({}, '', cleanUrl.toString());
            } catch (stripeErr) {
              console.warn('[LoadingScreen] Stripe checkout sync failed:', stripeErr);
            }
          }
        }

        // Show loading immediately when session detection starts
        await updateProgress(0.1, 'Detecting session...');
        const session = await apis.getSession();

        if (session?.data) {
          const userId = store.getState()?.promiseData?.data?.userData?.id;
          if (session.data.company?._id) {
            await recordOnlineContextAccess(
              buildSessionScopeId({ companyId: session.data.company._id, userId })
            );
          } else if (session.data.targetUser) {
            const targetUserId =
              session.data.targetUser._id || session.data.targetUser.id;
            if (targetUserId) {
              await recordOnlineContextAccess(
                buildSessionScopeId({ targetUserId, userId })
              );
            }
          } else if (userId) {
            await recordOnlineContextAccess(
              buildSessionScopeId({ userId })
            );
          }
        }

        //Apply company or target user stored in session as default
        // PRIORITY: companyNumber > targetUserId > personal account
        if (session?.data?.company) {
          const item = session.data.company;
          dispatch({ type: SWITCH_COMPANY_REQUEST, payload: { id: item._id, logo: item.logo, companyName: item.company_name || item.copmany_name } });
        } else if (session?.data?.targetUser) {
          // Target user context - clear company selection (target user is personal account mode)
          dispatch({ type: CLEAR_COMPANY });
          applyTargetUserSessionContext(session.data);
        } else {
          // Personal account - ensure company is cleared
          dispatch({ type: CLEAR_COMPANY });
        }

        await updateProgress(0.3, 'Loading session');
        const { isAuthenticated } = session?.data || {};

        await updateProgress(0.7, 'Loading user');

        // After awaits, hook-selected `user` can be stale (persist rehydration / first paint).
        // Session cookie is source of truth for auth; hydrate profile from store then refresh-user if needed.
        let userData = store.getState().promiseData?.data?.userData;
        // A reload between login and the persisted SUCCESS write leaves isAuthenticated=false
        // (or a profile shell without id); DashStack would bounce straight back to Login.
        const cachedPromiseData = store.getState().promiseData;
        if (isAuthenticated && (!cachedPromiseData?.isAuthenticated || !userData?.id)) {
          try {
            const freshUser = await apis.fetchCurrentUser();
            if (freshUser?.id && (!session?.data?.userId || freshUser.id === session.data.userId)) {
              dispatch({
                type: SUCCESS,
                data: { ...(cachedPromiseData?.data || {}), userData: freshUser },
              });
              userData = store.getState().promiseData?.data?.userData;
            }
          } catch (restoreErr) {
            console.warn('[LoadingScreen] Could not restore session profile:', restoreErr);
          }
        }
        if (isAuthenticated && !userData) {
          try {
            await apis.refreshUser();
            userData = store.getState().promiseData?.data?.userData;
          } catch (refreshErr) {
            console.warn('[LoadingScreen] refreshUser failed while session reports authenticated:', refreshErr);
          }
        }

        if (isAuthenticated && userData) {
          if (
            !session?.data?.company &&
            !session?.data?.targetUser &&
            userData.id
          ) {
            await recordOnlineContextAccess(
              buildSessionScopeId({ userId: userData.id })
            );
          }

          // Check TC modal state before navigating
          const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
          console.log('[LoadingScreen] TC modal state:', tcModalVisible);
          
          if (tcModalVisible) {
            console.log('[LoadingScreen] TC modal visible, blocking navigation to Dashboard');
            return;
          }

          // Fetch companies if not already present in user data
          const companiesData = userData.companies;
          if (!companiesData || !Array.isArray(companiesData) || companiesData.length === 0) {
            console.log("📋 Companies not found in user data, fetching companies...");
            try {
              const companiesResponse = await apis.getUserCompanies();
              if (companiesResponse?.success && companiesResponse?.companies) {
                dispatch({
                  type: UPDATE_USER,
                  payload: { companies: companiesResponse.companies }
                });
                console.log("✅ Companies fetched and updated:", companiesResponse.companies.length);
              }
            } catch (companiesError) {
              console.warn("⚠️ Failed to fetch companies (continuing anyway):", companiesError);
              // Continue even if companies fetch fails
            }
          } else {
            console.log("✅ Companies already in user data:", companiesData.length);
          }

          console.log("[LoadingScreen] User is authenticated, loading dashboard");
          const authUserId = session?.data?.userId ?? userData?.id;
          void persistAuthSession({
            userId: authUserId,
          });
          await updateProgress(1, 'Loading Dashboard');
          markSessionResolved();
          safeNavigate('Dashboard');
        } else if (!isAuthenticated) {
          console.log("User not authenticated, navigating to login");
          await updateProgress(1, 'Redirecting to login');
          const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
          markSessionResolved();
          if (isLearnRoute) {
            safeNavigate('Learn', { learningMode: true });
          } else {
            safeNavigate('Login', {});
          }
          void clearStaleAuthIfNeeded();
        } else {
          // Server session OK but no user payload (e.g. refresh failed) — do not PURGE persisted state
          console.warn('[LoadingScreen] Authenticated session without user profile; opening login without purge');
          await updateProgress(1, 'Redirecting to login');
          const isLearnRouteElse = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
          markSessionResolved();
          if (isLearnRouteElse) {
            safeNavigate('Learn', { learningMode: true });
          } else {
            safeNavigate('Login', {});
          }
        }
      } catch (error) {
        console.warn("Error fetching session:", error);

        // Network/timeouts: keep cached auth when we have it — do not purge on refresh blips.
        if (isTransientRequestError(error as any)) {
          const cachedUser = store.getState().promiseData?.data?.userData;
          const cachedAuth =
            Boolean(store.getState().promiseData?.isAuthenticated) ||
            hasPersistedWebSession();
          if (cachedAuth && cachedUser?.id) {
            console.warn(
              '[LoadingScreen] Transient session probe failure; continuing with cached auth'
            );
            await updateProgress(1, 'Loading Dashboard');
            markSessionResolved();
            safeNavigate('Dashboard');
            return;
          }
        }

        const definiteUnauth = isDefiniteSessionProbeFailure(undefined, error as any);
        await updateProgress(1, "No session found");
        const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
        markSessionResolved();
        if (isLearnRoute) {
          safeNavigate('Learn', { learningMode: true });
        } else {
          safeNavigate('Login', {});
        }
        if (Platform.OS === 'web' && definiteUnauth) {
          clearSessionCookie();
        }
        if (definiteUnauth || !isTransientRequestError(error as any)) {
          void clearStaleAuthIfNeeded();
        }
      }
    };

    if (!holdFlag) {
      fetchSession();
    }
  }, [clearStaleAuthIfNeeded, holdFlag, markSessionResolved, updateProgress]);

  const statusLabel = holdFlag ? loadingMessage : (text || 'Loading...');

  const webCardStyle = useMemo(
    () => ({
      width: '100%' as const,
      maxWidth: 360,
      borderRadius: 24,
      paddingVertical: 28,
      paddingHorizontal: 24,
      alignItems: 'center' as const,
      backgroundColor: theme ? 'rgba(255, 255, 255, 0.92)' : 'rgba(18, 22, 38, 0.88)',
      borderWidth: 1,
      borderColor: theme ? 'rgba(15, 23, 42, 0.08)' : 'rgba(255,255,255,0.12)',
      ...(Platform.OS === 'web' ? ({ backdropFilter: 'blur(12px)' } as any) : {}),
    }),
    [theme]
  );

  if (Platform.OS === 'web') {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: 24,
          backgroundColor: WEB_LOADING_BG,
        }}
      >
        <View style={webCardStyle}>
          <ActivityIndicator size="large" color="rgb(90,192,234)" />
          <Text
            style={{
              color: theme ? '#0f172a' : '#f1f5f9',
              fontSize: 16,
              textAlign: 'center',
              fontWeight: '600',
              marginTop: 16,
              minHeight: 22,
            }}
          >
            {statusLabel}
          </Text>
        </View>
      </View>
    );
  }

  const { width, height } = nativeDimsRef.current;

  return (
    <View style={{ flex: 1, overflow: 'visible' }}>
      <View style={[styles.background, { position: 'absolute', minWidth: '250%' }]}>
        <LottieView
          resizeMode="cover"
          style={{ alignSelf: 'stretch', overflow: 'visible', width: width * 2.4, height: height * 1.1 }}
          source={theme ? require('@assets/animations/backgroundLottie.json') : require('@assets/animations/backgroundLottieDark.json')}
          autoPlay
          loop
        />
      </View>
      <Animated.View
        style={{
          flex: 1,
          opacity: 1,
          margin: 100,
          top: height / 6.5,
          position: 'absolute',
          maxWidth: '70%',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
        }}
      >
        <View
          style={{
            width: '100%',
            borderRadius: 24,
            paddingVertical: 24,
            paddingHorizontal: 20,
            backgroundColor: theme ? 'rgba(18, 22, 38, 0.45)' : 'rgba(18, 22, 38, 0.8)',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.12)',
          }}
        >
          <LottieView
            style={{ flex: 1, minWidth: 250, minHeight: 300 }}
            source={require('@assets/animations/landing_Animation_dark.json')}
            autoPlay
            loop={false}
          />
          <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 16, textAlign: 'center', fontWeight: '600', marginTop: -8 }}>
            {statusLabel}
          </Text>
        </View>
        <View style={{ width: '50%', margin: 30, alignItems: 'center' }}>
          <ActivityIndicator size="large" color="rgb(90,192,234)" />
        </View>
      </Animated.View>
    </View>
  );
}

export default LoadingScreen;